import { formatMoney } from '@wit-support/ui-domain';
/**
 * Атрибуты кейса и его алертов, по которым оператор ГЛАЗАМИ сверяет кандидата.
 *
 * Это ФАКТЫ АЛЕРТА, а не «то, по чему мы искали», и путать эти две вещи нельзя.
 * Адаптер отправляет матчеру не всё, что видит: значение, не прошедшее его
 * валидацию (код авторизации с посторонними символами, непохожие на себя четыре
 * цифры, дробная сумма), в запрос не уезжает вовсе. Подписать поле алерта
 * словами «искали по коду авторизации» значило бы утверждать то, чего мы не
 * знаем, — ровно та неправда, от которой ушла строка «Не сошлось».
 *
 * ПРАВИЛО, А НЕ СПИСОК. Показывается ВСЁ непустое из опознавательных полей.
 * Закрытый перечень уже однажды промахнулся: у боевого RDR-алерта Visa в
 * карточке кейса стояли BIN, ARN и страна, а в диалоге их не было — то есть
 * оператор не видел ровно тех примет, по которым и опознают. Пустые поля не
 * рисуются вовсе, значит за отсутствующие цены нет, а за отсутствующее правило
 * есть.
 *
 * Не показывается СОСТОЯНИЕ ОБРАБОТКИ: `isMatched`, `isRefunded`, `reasonCode`,
 * «требует действия», `acquirerAction`, идентификаторы наших строк. Это не
 * приметы транзакции, и в задаче «найти клиента» они не участвуют.
 */
const clean = (v) => {
    const t = v?.trim() ?? '';
    return t === '' ? null : t;
};
/**
 * Различные непустые значения поля по всем алертам кейса, в порядке появления.
 *
 * Именно РАЗЛИЧНЫЕ, а не первое попавшееся: у кейса бывает несколько алертов из
 * разных сетей, и подмножества заполненных полей у них не совпадают (у одного
 * есть ARN, у другого — идентификатор транзакции в сети). Молча склеить их в
 * одно значение значит показать оператору один код авторизации там, где их два,
 * — и он будет сверять кандидата не с тем.
 */
export function distinctAlertValues(alerts, field) {
    const seen = [];
    for (const a of alerts) {
        const v = a[field];
        const s = clean(typeof v === 'string' ? v : null);
        if (s != null && !seen.includes(s))
            seen.push(s);
    }
    return seen;
}
/**
 * Почему страна алерта показывается, но сверять по ней нельзя.
 *
 * Платёжный сервис её не принимает вовсе, и это обосновано замером: на 1739
 * подтверждённых боевых матчах поле расходится со страной эмитента карты в 25%
 * случаев, и в 7,6% даже там, где совпадение установлено по глобально
 * уникальному идентификатору транзакции. Типичные пары `US→BR`, `ES→US`,
 * `RU→MX` — это гео провайдера, а не страна BIN.
 *
 * Прятать поле от человека неправильно: это факт алерта. Но оператор, который
 * отвергнет верного кандидата со словами «алерт из Чили, а карта из США»,
 * потеряет привязку на ровном месте — по замеру каждый четвёртый раз.
 */
export const COUNTRY_WARNING = {
    inline: 'не для сверки',
    title: 'Почему стране алерта нельзя верить',
    body: 'Платёжный сервис страну алерта не принимает вовсе. На 1739 подтверждённых боевых матчах она расходится со страной эмитента карты в 25% случаев — и в 7,6% даже там, где совпадение установлено по глобально уникальному идентификатору транзакции. Типичные пары US→BR, ES→US, RU→MX: это гео провайдера, а не страна BIN.',
};
/**
 * Доказательные приметы — идут первыми. Разделение на два списка задаёт ПОРЯДОК
 * и только его: показываются оба целиком и всегда. (Раньше второй список был
 * спрятан под раскрытием «ещё»; замер показал, что раскрытие стоит 20 px и ни
 * одного видимого кандидата, то есть прятало без выгоды — а лишнее состояние
 * оператор обязан был переключать, чтобы увидеть данные, за которыми и пришёл.)
 */
const STRONG_FIELDS = [
    { key: 'authCode', field: 'authCode', label: 'Код авторизации', mono: true },
    { key: 'networkId', field: 'networkId', label: 'ID в сети', mono: true },
    { key: 'arn', field: 'arn', label: 'ARN', mono: true },
    { key: 'bin', field: 'bin', label: 'BIN', mono: true },
];
/** Остальные приметы. Страна замыкает — см. комментарий у неё. */
const OTHER_FIELDS = [
    {
        key: 'providerEmail',
        field: 'providerCustomerEmail',
        label: 'Почта у провайдера',
        mono: false,
    },
    {
        key: 'providerCustomerId',
        field: 'providerCustomerId',
        label: 'ID клиента у провайдера',
        mono: true,
    },
    { key: 'externalOrder', field: 'externalOrder', label: 'Заказ', mono: true },
    { key: 'funding', field: 'funding', label: 'Тип карты', mono: false },
    { key: 'issuer', field: 'issuer', label: 'Эмитент', mono: false },
    { key: 'mid', field: 'mid', label: 'MID', mono: true },
    { key: 'processor', field: 'processor', label: 'Процессинг', mono: false },
    // Страна замыкает: она единственная, по которой сверять нельзя, и стоять
    // среди примет, которым верят, ей не место.
    {
        key: 'country',
        field: 'country',
        label: 'Страна',
        mono: false,
        unreliable: true,
    },
];
/**
 * Факты алерта. Дата возвращается СЫРОЙ ISO-строкой под ключом `date`: её
 * формат принадлежит киту (`Time` знает часовой пояс оператора), и повторять
 * его здесь значило бы завести второй формат дат.
 */
export function alertFacts(detail) {
    const c = detail.case;
    const facts = [];
    const last4 = clean(c.cardLast4);
    if (last4 != null) {
        // Бренд приклеен к цифрам, а не отдельным полем: «visa •••7703» — то, как
        // оператор читает карту в выписке, и разносить это на две подписи значит
        // заставлять его собирать их обратно глазами.
        const brands = distinctAlertValues(detail.alerts, 'cardBrand');
        facts.push({
            key: 'card',
            label: 'Карта',
            values: [brands.length > 0 ? `${brands.join(' / ')} •••${last4}` : `•••${last4}`],
            mono: true,
        });
    }
    if (c.amountCents != null && c.currency) {
        facts.push({
            key: 'amount',
            label: 'Сумма',
            values: [formatMoney(c.amountCents, c.currency)],
            mono: true,
        });
    }
    if (c.transactionAt != null) {
        facts.push({
            key: 'date',
            label: 'Дата',
            values: [c.transactionAt],
            mono: false,
        });
    }
    const pushFields = (fields) => {
        for (const f of fields) {
            const values = distinctAlertValues(detail.alerts, f.field);
            if (values.length === 0)
                continue;
            facts.push({
                key: f.key,
                label: f.label,
                values,
                mono: f.mono,
                ...(f.unreliable ? { unreliable: f.unreliable } : {}),
            });
        }
    };
    pushFields(STRONG_FIELDS);
    const descriptor = clean(c.descriptor);
    if (descriptor != null) {
        facts.push({ key: 'descriptor', label: 'Дескриптор', values: [descriptor], mono: true });
    }
    pushFields(OTHER_FIELDS);
    return facts;
}
//# sourceMappingURL=alert-facts.js.map